import axios from '../../common/utils/interceptor';
import handleClickAwayLanguageSelection from '../utils/handleClickAwayLanguageSelection';
import markSchedulableItems from '../utils/markSchedulableItems';
import React, { useEffect, useState } from 'react';
import taskItemStatusMapper from '../utils/taskItemStatusMapper';
import updateTaskItem from '../utils/updateTaskItem';
import { apiUrls, routeMappings, WorkflowItemAssessmentCodes, WorkflowItemCode } from '../../constants';
import { AssessmentDetails, Task, TaskItem } from '../interfaces';
import { createLaunchConfig } from '../../common/utils/launchConfig';
import { FullPageLoader } from '../../common/components/FullPageLoader';
import { Language } from '../../common/interfaces/language';
import { RoutePageProps } from '../../base/components/Routes';
import { SchedulableItem } from '../interfaces/taskList';
import { SessionStore } from '../../constants/sessionStore';
import { TaskItemStatus } from '../constants';
import { useBasicDetails } from '../../store/storeApi';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ValidateAssessmentLaunchConfig } from '../../assessment/interfaces';
import { getNextNotStartedOrInProgressItem } from '../utils/getNextNotStartedOrInProgressItem';
import { fetchAssessmentDetail } from '../utils/fetchAssessmentDetail';
import { fetchLaunchConfigOfCompletedItemFromTask } from '../utils/fetchLaunchConfigOfCompletedItemFromTask';
import { persistPreviouslySelectedLanguage } from '../utils/persistPreviouslySelectedLanguage';
import { markCurrentNonAssessmentItemDone } from '../utils/markCurrentNonAssessmentItemDone';
import { getVideoFeedbackStatus } from '../utils/getVideoFeedbackStatus';
import { markWorkflowEnd } from '../utils/markWorkflowEnd';
import { getAssessmentUrlApp } from '../utils/getAssessmentUrlApp';
import { getDisplayOptionsStatus } from '../utils/getDisplayOptionsStatus';
import { SHLApp } from '../../constants/shlapp';
import { getNextLinearTask } from '../utils/getNextLinearTask';
import { initializeTask } from '../utils/initializeTask';
import localizeContentMapper from '../../common/utils/localizeContentMapper';
import firstlaunchConfigOfCompletedItemOfAnyTask from '../utils/firstlaunchConfigOfCompletedItemOfAnyTask';
import { deleteCookie, getCookie } from '../../common/utils/cookies';
import { useQuery } from '../../common/hooks';

let timeoutId: NodeJS.Timeout;
let isNextItemCalled = false;

/**
 * HOC Responsible
 * - Go to next workflow item
 * - End the task workflow for last item and redirect back to initiator page
 */
const withTaskFlow = <P extends RoutePageProps>(Component: React.ComponentType<P>): ((props: P) => JSX.Element) => (
  props: P,
): JSX.Element => {
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const routeParams = useParams<{ id?: string; itemId?: string }>();
  const {
    currentWorkflowItem: { id, configuration, nestedItemsState, localizedContentMap },
    userSessionActive,
    updateCurrentWorkflowItem,
  } = useBasicDetails();

  const [isFetchingWorkflowItem, setIsFetchingWorkflowItem] = useState(false);
  const [isLanguageSelectionFormOpen, setLanguageSelectionFormOpen] = useState(false);
  const [nextTaskDetails, setNextTaskDetails] = useState<{ task?: Task; item?: TaskItem }>({});
  const [assessmentDetail, setAssessmentDetail] = useState<AssessmentDetails>({
    availableLanguages: [],
    activeComponentId: 0,
    projectId: 0,
    packageId: 0,
    displayOptions: [],
    selectedLanguageCode: 'EN_US',
    sittingId: 0,
    defaultPackageLanguage: {
      testId: 0,
      language: {
        default: false,
        description: '',
        id: 0,
        integrationIsoCode: '',
        isoCode: '',
        name: '',
      },
    },
  });

  let currentTaskItemIndex = 0;
  let returnToTaskList = false;
  let currentTask: Task | undefined;
  let currentItem: TaskItem;
  let itemStatusIntervalId: ReturnType<typeof setInterval>;
  let cfcId: number;
  let linearflow = false;
  let showTaskList = true;
  let tasks: Task[] = [];
  if (configuration && routeParams.id && routeParams.itemId) {
    const taskId = routeParams.id;
    const itemId = routeParams.itemId;
    const config = JSON.parse(configuration);

    cfcId = config.configWidgetJSON.feedbackPage?.cfc;
    linearflow = config.configWidgetJSON.linearflow === 'true';
    showTaskList = config.configWidgetJSON.showTaskList === 'true';
    const parsedConfig = localizeContentMapper(config.configWidgetJSON, localizedContentMap);
    const tasksWithStatus = taskItemStatusMapper(parsedConfig.tasks, nestedItemsState);
    tasks = tasksWithStatus;
    currentTask = tasksWithStatus.find((task: Task) => task.id === +taskId);
    if (currentTask) {
      currentTaskItemIndex = currentTask.items.findIndex((item: TaskItem) => item.id == itemId); // eslint-disable-line
      currentItem = currentTask.items[currentTaskItemIndex];
      if (
        currentItem &&
        currentItem.code === WorkflowItemCode.GENCONTENT &&
        (currentTaskItemIndex !== currentTask.items.length - 1 || (linearflow && showTaskList)) &&
        (!linearflow || (linearflow && showTaskList))
      )
        returnToTaskList = true;
    }
  }

  const checkIfVideoFeedbackRequestSent = async (): Promise<boolean> => {
    try {
      const { data } = await axios.get(apiUrls.GET_VIDEO_FEEDBACK_GENERATION_REQUEST_STATUS);
      if (data.readyForGeneration) {
        sessionStorage.setItem(SessionStore.isVideoFeedbackGenerationRequested, 'true');
        return true;
      }
    } catch (e) {
      console.error('Video Feedback Generation Request status error: ', e);
    }
    return false;
  };

  // This function redirects to gen content if next item is gen content
  const goToNextNonAssessmentItem = async (): Promise<void> => {
    if (currentTask) {
      const nextItem = getNextNotStartedOrInProgressItem(currentTask, currentTaskItemIndex);
      if (!nextItem) {
        history.push(`${routeMappings['TASKLIST']}/${id}`);
        return;
      }
      if (nextItem.status !== TaskItemStatus.COMPLETED) {
        const params = { nextItemId: nextItem.id };
        const { data } = await axios.put(`${apiUrls.INITIALIZE_TASK}/${currentItem.id}/next`, null, {
          params,
          headers: { 'Content-Type': 'application/json' },
        });
        // Update sessionStorage with next item and mark it IN_PROGRESS
        updateTaskItem({ ...data, taskId: currentTask.id }, TaskItemStatus.IN_PROGRESS);
      }

      history.push(`${routeMappings['TASK']}/${currentTask.id}${routeMappings[nextItem.code]}/${nextItem.id}`);
    }
  };

  const navigateToQuizFeedback = (taskItem: TaskItem): boolean => {
    if (
      currentTask &&
      taskItem.code === WorkflowItemCode.EXPQUIZ &&
      taskItem.status === TaskItemStatus.COMPLETED &&
      !(
        location.pathname.includes(routeMappings['EXPQUIZ']) &&
        location.pathname.includes(routeMappings['QUIZ_FEEDBACK'])
      )
    ) {
      history.push(
        `${routeMappings['TASK']}/${currentTask.id}${routeMappings[taskItem.code]}/${taskItem.id}${
          routeMappings['QUIZ_FEEDBACK']
        }`,
      );
      return true;
    }
    return false;
  };

  const goToNextAssessmentItem = async (
    nextItem: TaskItem,
    launchAssessmentConfig?: ValidateAssessmentLaunchConfig,
  ): Promise<void> => {
    if (!currentTask) return;

    if (!nextItem.state && !launchAssessmentConfig) {
      let launchConfig: string | undefined;

      const assessmentDetails = await fetchAssessmentDetail(nextItem);
      setAssessmentDetail(assessmentDetails);

      if (!nextItem.isSchedulable) {
        if (linearflow) {
          launchConfig = firstlaunchConfigOfCompletedItemOfAnyTask(currentTask, nextItem, assessmentDetails);
        } else {
          launchConfig = fetchLaunchConfigOfCompletedItemFromTask(currentTask.items, currentTaskItemIndex);
        }

        if (!launchConfig) {
          // If nextItem is the first quiz or assessment in the task
          if (assessmentDetails.availableLanguages.length > 1) setLanguageSelectionFormOpen(true);
          return;
        } else {
          // Method to persist previoulsy selected language in nextItem
          launchConfig = persistPreviouslySelectedLanguage(
            launchConfig,
            nextItem.id,
            currentTask.id,
            assessmentDetails,
          );
          // LF new persistPreviouslySelectedLanguage
        }

        if (!WorkflowItemAssessmentCodes.includes(currentItem.code))
          await markCurrentNonAssessmentItemDone(currentTask, currentTaskItemIndex);

        if (userSessionActive || nextItem.code === WorkflowItemCode.EXPQUIZ) {
          updateTaskItem({ ...nextItem, taskId: currentTask.id, state: launchConfig }, TaskItemStatus.IN_PROGRESS);
          try {
            const parsedLaunchConfig = JSON.parse(launchConfig);
            if (launchConfig && (await getAssessmentUrlApp(parsedLaunchConfig))) return;
          } catch (e) {
            console.error('Error in parsing launch config', e);
          }

          // LF change currentTask
          history.push(`${routeMappings['TASK']}/${currentTask.id}${routeMappings[nextItem.code]}/${nextItem.id}`);
        } else {
          // Quiz to assessment (if not logged in)
          history.push(`${routeMappings['LOGIN']}`);
        }
      }
    } else {
      // nestedItemState is already present
      const currentItemState = nextItem.state && { ...JSON.parse(nextItem.state), taskId: currentTask.id };
      const launchConfig = currentItemState || launchAssessmentConfig;
      sessionStorage.setItem(SessionStore.launchconfig, JSON.stringify(launchConfig));

      await markCurrentNonAssessmentItemDone(currentTask, currentTaskItemIndex);

      // Update sessionStorage with fetched item and mark it IN_PROGRESS
      updateTaskItem(
        { ...nextItem, taskId: currentTask.id, state: JSON.stringify(launchConfig) },
        TaskItemStatus.IN_PROGRESS,
      );

      if (navigateToQuizFeedback(nextItem)) return;

      // if session is active redirect to launch assessment
      if (userSessionActive || nextItem.code === WorkflowItemCode.EXPQUIZ) {
        history.push(
          `${routeMappings['TASK']}/${launchConfig.taskId}${routeMappings[nextItem.code]}/${launchConfig.itemId}`,
        );
      } else {
        history.push(`${routeMappings['LOGIN']}`);
      }
    }
  };

  const goToNextAssessmentItemFromNextTask = async (task: Task, item: TaskItem): Promise<void> => {
    const assessmentDetails = await fetchAssessmentDetail(item);
    setAssessmentDetail(assessmentDetails);

    if (!item.isSchedulable) {
      if (assessmentDetails.availableLanguages.length > 1) {
        setLanguageSelectionFormOpen(true);
        setNextTaskDetails({ task, item });
      } else {
        const launchConfig = createLaunchConfig(
          item.id,
          task.id,
          assessmentDetails.availableLanguages[0],
          assessmentDetails,
        );
        initializeTask(task, userSessionActive, history, location, launchConfig);
      }
    }
  };

  /**
   * This mehtod will invoked in two scenarios mentioned below:
   * - if scoring gets completed no matter if it fails
   * - if current item is the last item in the task
   */
  const redirectOnAssessmentEvaluationOrTaskComplete = async (): Promise<void> => {
    const currentWorkflowItem = sessionStorage.getItem(SessionStore.currentWorkflowItem);
    if (currentWorkflowItem && currentTask) {
      const parsedCurrentWorkflowItem = JSON.parse(currentWorkflowItem);
      const config = JSON.parse(parsedCurrentWorkflowItem.configuration);
      const tasks = taskItemStatusMapper(config.configWidgetJSON.tasks, nestedItemsState);
      const isAnyTaskHurdleFailed = tasks.some((i: Task) => i.status === TaskItemStatus.HURDLE_FAILED);
      const isWorkflowCompleted = tasks.every((i: Task) => i.optional || i.status === TaskItemStatus.COMPLETED);
      const nextItem = getNextNotStartedOrInProgressItem(currentTask, currentTaskItemIndex);
      const isFeedbackAvailable = config.configWidgetJSON.feedbackPage?.enabled;

      const isBasicFlow = parsedCurrentWorkflowItem.code === WorkflowItemCode.BASICTASKLIST;
      const updatedCurrentTask = tasks.filter((item) => item.id === currentTask?.id)[0];
      const isLastTask = tasks.findIndex((task) => task.id === currentTask?.id) === tasks.length - 1;
      // Check for video feedback after all the tasks except last is left
      const completedTasks = tasks.filter((task: Task) => task.status === TaskItemStatus.COMPLETED).length;
      if (completedTasks === tasks.length - 1 && isFeedbackAvailable) {
        getVideoFeedbackStatus(cfcId);
      }
      if (isBasicFlow && updatedCurrentTask && updatedCurrentTask.status === TaskItemStatus.COMPLETED && !isLastTask) {
        history.push(routeMappings['TASK_COMPLETION']);
      } else if (isAnyTaskHurdleFailed) {
        await markWorkflowEnd();
        let path = `${parsedCurrentWorkflowItem.code}_HURDLE_FAIL`;
        if (isFeedbackAvailable && (await checkIfVideoFeedbackRequestSent())) {
          path = 'VIDEO_FEEDBACK';
        }
        history.push(routeMappings[path]);
      } else if (isWorkflowCompleted) {
        await markWorkflowEnd();
        const path = isFeedbackAvailable ? 'VIDEO_FEEDBACK' : `${parsedCurrentWorkflowItem.code}_END`;
        history.push(routeMappings[path]);
      } else if (nextItem && !isBasicFlow) {
        if (WorkflowItemAssessmentCodes.includes(nextItem.code)) {
          goToNextAssessmentItem(nextItem);
        } else {
          goToNextNonAssessmentItem();
        }
      } else if (linearflow) {
        const task = getNextLinearTask(tasks);
        if (task) {
          const firstNotStartedOrInProgressItem = task.items.find(
            (item) =>
              item.status === TaskItemStatus.IN_PROGRESS || !item.status || item.status === TaskItemStatus.NOT_STARTED,
          );
          const nextTaskItem = firstNotStartedOrInProgressItem ? firstNotStartedOrInProgressItem : task.items[0];
          let firstAssessment: TaskItem | undefined;

          if (WorkflowItemAssessmentCodes.includes(nextTaskItem.code) && !nextTaskItem.state) {
            for (let i = 0; i < tasks.length; i++) {
              firstAssessment = tasks[i].items.find((item) => WorkflowItemAssessmentCodes.includes(item.code));
              if (firstAssessment) {
                break;
              }
            }
          }

          // Only for first assessment in the entire workflow
          if (firstAssessment?.id === nextTaskItem.id) {
            goToNextAssessmentItemFromNextTask(task, nextTaskItem);
          } else {
            initializeTask(task, userSessionActive, history, location);
          }
        }
      } else {
        history.push(`${routeMappings[parsedCurrentWorkflowItem.code]}/${id}`);
      }
    }
  };

  const evaluateAssessment = (taskItem: TaskItem): void => {
    itemStatusIntervalId = setInterval(async () => {
      const { data } = await axios.get(`${apiUrls.INITIALIZE_TASK}/${taskItem.id}`);
      if (data && data.status !== TaskItemStatus.EVALUATION_PENDING) {
        clearInterval(itemStatusIntervalId);
        if (data.status === TaskItemStatus.HURDLE_FAILED) {
          updateTaskItem({ ...taskItem, taskId: taskItem.taskId }, TaskItemStatus.HURDLE_FAILED);
        } else {
          updateTaskItem({ ...taskItem, taskId: taskItem.taskId }, TaskItemStatus.COMPLETED);
        }
        redirectOnAssessmentEvaluationOrTaskComplete();
      }
    }, 3000);
  };

  /**
   * This is for the purpose of restarting evaluation
   * when user refreshes on score evaluation page.
   */
  useEffect(() => {
    if (currentTask) {
      if (
        WorkflowItemAssessmentCodes.includes(currentItem.code) &&
        currentItem.status === TaskItemStatus.EVALUATION_PENDING
      ) {
        evaluateAssessment({ ...currentItem, taskId: currentTask.id });
      }
    }

    return (): void => {
      if (itemStatusIntervalId) clearInterval(itemStatusIntervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask, currentTaskItemIndex]);

  const isSchedulablePackageAvailable = (): TaskItem | null => {
    const currentWorkflowItem = sessionStorage.getItem(SessionStore.currentWorkflowItem);
    if (currentWorkflowItem && routeParams.id && routeParams.itemId) {
      const taskId = routeParams.id;
      const config = JSON.parse(JSON.parse(currentWorkflowItem).configuration);
      const schedulableAssessment = sessionStorage.getItem(SessionStore.schedulableAssessments);
      const scheduledAssessmentData: SchedulableItem[] = schedulableAssessment && JSON.parse(schedulableAssessment);
      const tasks = markSchedulableItems(config.configWidgetJSON.tasks, scheduledAssessmentData);
      const currentTask = tasks.find((task: Task) => task.id === +taskId);
      if (currentTask) {
        const nextItem = getNextNotStartedOrInProgressItem(currentTask, currentTaskItemIndex);
        if (nextItem && nextItem.code === WorkflowItemCode.EXPASSES && nextItem.isSchedulable) {
          return nextItem;
        }
      }
    }
    return null;
  };

  const isNextItemAssessment = (): boolean => {
    if (currentTask) {
      const nextItem = getNextNotStartedOrInProgressItem(currentTask, currentTaskItemIndex);
      if (nextItem && nextItem.code === WorkflowItemCode.EXPASSES) {
        return true;
      }
    }
    return false;
  };

  /**
   * This method handles case when the current item is an assessment
   * If it's not complete, go to task list
   * If evaluation pending, wait...
   * TODO: Hurdle fail scenario not handled
   */
  const continueOnlyIfAssessmentComplete = async (): Promise<boolean> => {
    if (currentTask) {
      const { data: currentItemState } = await axios.get(`${apiUrls.INITIALIZE_TASK}/${currentItem.id}`);

      if (currentItemState) {
        currentItem.state = currentItemState.state;
        currentItem.status = currentItemState.status;

        updateTaskItem(
          {
            ...currentItem,
            state: currentItemState.state,
            taskId: currentTask.id,
            ...currentItemState.workflowItemConfiguration,
          },
          currentItemState.status,
        );

        if (currentItemState.status === TaskItemStatus.HURDLE_FAILED) {
          redirectOnAssessmentEvaluationOrTaskComplete();
        } else if (currentItemState.status === TaskItemStatus.EVALUATION_PENDING) {
          evaluateAssessment({ ...currentItem, state: currentItemState.state, taskId: currentTask.id });
          history.push(
            `${routeMappings['TASK']}/${currentTask.id}${routeMappings[currentItem.code]}/${currentItem.id}${
              routeMappings['ASSESSMENT_EVALUATION']
            }`,
          );

          return true;
        } else if (
          currentItemState.status !== TaskItemStatus.COMPLETED &&
          currentItemState.status !== TaskItemStatus.HURDLE_PASSED
        ) {
          // Current assessment still in progress (meaning user exited)
          sessionStorage.setItem(SessionStore.allowRedirectToTasklist, 'true');
          history.push(`${routeMappings['TASKLIST']}/${id}`);

          return true;
        }
      }
    }

    return false;
  };

  /**
   * Method to go to next workflow item
   *  - Pass the current workflow item id as request params,
   *  - Get next workflow item details
   *  - Update the sessionStorage & store to redirect to next workflow item
   */
  const getNextWorkflowItem = async (launchAssessmentConfig?: ValidateAssessmentLaunchConfig): Promise<void> => {
    try {
      if (currentTask) {
        const nextItem = getNextNotStartedOrInProgressItem(currentTask, currentTaskItemIndex);
        // LF new function to get next item from all tasks

        if (WorkflowItemAssessmentCodes.includes(currentItem.code)) {
          // Check if current item is an assessment and it's complete/evaluation pending
          if (await continueOnlyIfAssessmentComplete()) return;
          if (navigateToQuizFeedback(currentItem)) return;
        }
        //Redirect to basic task list after completion of assesment
        let isBasic = false;
        if (configuration) {
          const config = JSON.parse(configuration);
          if (config.code === WorkflowItemCode.BASICTASKLIST) {
            isBasic = true;
          }
        }

        if (nextItem && !isBasic) {
          // Next item is assessment/quiz
          if (WorkflowItemAssessmentCodes.includes(nextItem.code)) {
            goToNextAssessmentItem(nextItem, launchAssessmentConfig);
          } else {
            // Next item is gen content
            // Current item is gen content
            // Mark previous item status completed
            if (!WorkflowItemAssessmentCodes.includes(currentItem.code)) {
              updateTaskItem({ ...currentItem, taskId: currentTask.id }, TaskItemStatus.COMPLETED);
            }
            goToNextNonAssessmentItem();
          }
        } else {
          // No next item, currentItem is last item
          // current item is gen content
          if (!WorkflowItemAssessmentCodes.includes(currentItem.code)) {
            await markCurrentNonAssessmentItemDone(currentTask, currentTaskItemIndex);
          }
          redirectOnAssessmentEvaluationOrTaskComplete();
        }
      }
    } catch (error) {
      console.error('error getting next wfItem', error);
    }
  };

  const onLanguageSubmit = (selectedLanguage: Language): void => {
    if (nextTaskDetails.task?.id && nextTaskDetails.item?.id) {
      const launchAssessmentConfig = createLaunchConfig(
        nextTaskDetails.item.id,
        nextTaskDetails.task.id,
        selectedLanguage,
        assessmentDetail,
      );

      initializeTask(nextTaskDetails.task, userSessionActive, history, location, launchAssessmentConfig);
    } else if (currentTask) {
      const nextItem = getNextNotStartedOrInProgressItem(currentTask, currentTaskItemIndex);
      if (!nextItem) return;

      // creating launch config
      const launchAssessmentConfig = createLaunchConfig(
        nextItem.id,
        currentTask.id,
        selectedLanguage,
        assessmentDetail,
      );
      getNextWorkflowItem(launchAssessmentConfig);
    }
  };

  // Fetch the current WFI config if not present on Language change
  useEffect(() => {
    const fetchCurrentWorkflowItem = async (): Promise<void> => {
      if (currentTask) {
        setIsFetchingWorkflowItem(true);
        const { data } = await axios.get(`${apiUrls.INITIALIZE_TASK}/${currentItem.id}`);
        updateTaskItem(
          { taskId: currentTask.id, ...data, ...data.workflowItemConfiguration },
          TaskItemStatus.IN_PROGRESS,
        );
        const currentWorkflowItem = sessionStorage.getItem(SessionStore.currentWorkflowItem);
        if (currentWorkflowItem) updateCurrentWorkflowItem(JSON.parse(currentWorkflowItem));
        setIsFetchingWorkflowItem(false);
      }
    };

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      if (currentTask) {
        if (!currentItem.configuration && !WorkflowItemAssessmentCodes.includes(currentItem.code))
          fetchCurrentWorkflowItem();
      }
    }, 200);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask, currentTaskItemIndex]);

  useEffect(() => {
    if (assessmentDetail.availableLanguages.length === 1) {
      onLanguageSubmit(assessmentDetail.availableLanguages[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentDetail]);

  useEffect(() => {
    const endAssessmentAndMoveToNextItem = async (): Promise<void> => {
      // Send JWT token
      const responseData = await axios.post(apiUrls.POST_ASSESSMENT_END, {
        itemId: currentItem.id,
        state: currentItem.state,
        token: query.get('token'),
      });
      if (currentTask && responseData.status === 200) {
        if (responseData.data.recalled) {
          history.push(routeMappings['LINK_INVALID']);
          return;
        }
        isNextItemCalled = true;
        getNextWorkflowItem();
      }
    };
    if (
      getCookie('TcPlusFlag') === '1' &&
      location.pathname.includes(routeMappings['ASSESSMENT_EVALUATION']) &&
      query.get('token') &&
      !isNextItemCalled &&
      currentItem?.id
    ) {
      sessionStorage.removeItem(SessionStore.launchconfig);
      sessionStorage.removeItem(SessionStore.launchurl);
      endAssessmentAndMoveToNextItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask, location.pathname]);

  useEffect(() => {
    const isSHLAppAgent = navigator.userAgent.indexOf(SHLApp.IS_SHLAPP_AGENT) > -1;
    const launchconfig = sessionStorage.getItem(SessionStore.launchconfig);
    const isEntryExhausted = sessionStorage.getItem(SessionStore.isEntryExhausted) === 'true';

    const parsedLaunchconfig = launchconfig && JSON.parse(launchconfig);
    const isMobileApp = parsedLaunchconfig?.displayOptions
      ? getDisplayOptionsStatus(parsedLaunchconfig.displayOptions)
      : false;

    if (!isEntryExhausted && isSHLAppAgent && launchconfig && currentTask && isMobileApp) {
      deleteCookie('experience_url');
      getNextWorkflowItem();
      sessionStorage.removeItem(SessionStore.launchconfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask]);

  return (
    <>
      <FullPageLoader isLoading={isFetchingWorkflowItem} />
      <Component
        {...props}
        isNextItemAssessment={isNextItemAssessment()}
        linearflow={linearflow}
        showTaskList={showTaskList}
        isSchedulablePackageAvailable={isSchedulablePackageAvailable}
        returnToTaskList={returnToTaskList}
        currentWorkflowItem={currentTask && currentTask.items[currentTaskItemIndex]}
        getNextWorkflowItem={getNextWorkflowItem}
        isLanguageSelectionFormOpen={isLanguageSelectionFormOpen}
        languages={assessmentDetail.availableLanguages}
        onLanguageSubmit={onLanguageSubmit}
        handleClickAwayLanguageSelection={(e: React.MouseEvent<Document, MouseEvent>): void => {
          handleClickAwayLanguageSelection(e, setLanguageSelectionFormOpen);
        }}
        taskName={currentTask?.title}
        tasks={tasks}
      />
    </>
  );
};

export default withTaskFlow;
